import React, { useState, useEffect } from 'react';
import { Handshake, Ticket, FileSearch, HeartHandshake, Timer, Lock, Wallet, Megaphone, CheckCircle2, XCircle } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { CLIENT_ACTIONS } from '../../data/clientActions';

const ICONS = {
  'visite-commerciale': Handshake,
  'invitation-evenement': Ticket,
  'solliciter-mission': FileSearch,
  'plan-reconquete': HeartHandshake,
};

function blockedLabel({ cdLeft, scoreBlocked, cashBlocked, action, score }) {
  if (cdLeft > 0) return `Disponible dans ${cdLeft} j`;
  if (scoreBlocked && action.minScore != null && score < action.minScore) return `Requiert ${action.minScore}+ de fidélité`;
  if (scoreBlocked && action.maxScore != null) return `Réservé aux relations dégradées (< ${action.maxScore})`;
  if (cashBlocked) return 'Trésorerie insuffisante';
  return null;
}

/**
 * ClientActionsPanel — 28/07/2026 (retour user prod) : leviers d'action
 * commerciale proactifs sur un client récurrent. Visite, invitation,
 * sollicitation de mission (non garantie), plan de reconquête.
 */
export default function ClientActionsPanel({ client, data }) {
  const totalDays = useGameStore(s => s.totalDays || 0);
  const treasury = useGameStore(s => s.treasury || 0);
  const cooldowns = useGameStore(s => s.clientActionCooldowns) || {};
  const performClientAction = useGameStore(s => s.performClientAction);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { setFeedback(null); }, [client.id]);

  const score = (data && data.score) || 0;

  const handle = (action) => {
    const res = performClientAction(client.id, action.id);
    if (!res) return;
    setFeedback({
      ok: res.ok && res.success !== false,
      message: res.ok ? res.message : blockedLabel({
        cdLeft: res.daysLeft || 0,
        scoreBlocked: res.reason === 'score_too_low' || res.reason === 'score_too_high',
        cashBlocked: res.reason === 'treasury',
        action,
        score,
      }) || 'Action indisponible.',
    });
  };

  return (
    <section data-testid={`client-actions-${client.id}`}>
      <h3 className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5 mb-2">
        <Megaphone className="w-3 h-3" />
        Actions commerciales
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {CLIENT_ACTIONS.map(action => {
          const Icon = ICONS[action.id] || Handshake;
          const until = cooldowns[`${client.id}:${action.id}`] || 0;
          const cdLeft = Math.max(0, until - totalDays);
          const scoreBlocked = (action.minScore != null && score < action.minScore)
            || (action.maxScore != null && score >= action.maxScore);
          const cashBlocked = treasury < action.cost;
          const disabled = cdLeft > 0 || scoreBlocked || cashBlocked;
          const blockText = blockedLabel({ cdLeft, scoreBlocked, cashBlocked, action, score });
          return (
            <button
              key={action.id}
              type="button"
              onClick={() => !disabled && handle(action)}
              disabled={disabled}
              data-testid={`client-action-${action.id}`}
              className={`text-left px-3 py-2.5 rounded-md border transition-all ${
                disabled
                  ? 'bg-ink-800/40 border-ink-700 opacity-60 cursor-not-allowed'
                  : 'bg-ink-800 border-ink-600 hover:border-accent/60 hover:bg-ink-700/40'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs font-bold text-slate-100">
                  <Icon className="w-3.5 h-3.5 text-accent" />
                  {action.label}
                </span>
                <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                  <Wallet className="w-2.5 h-2.5" />
                  {action.cost > 0 ? `${action.cost.toLocaleString('fr-FR')} €` : 'Gratuit'}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{action.description}</p>
              {blockText ? (
                <p className="text-[10px] font-mono mt-1.5 flex items-center gap-1 text-amber-300/90">
                  {cdLeft > 0 ? <Timer className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
                  {blockText}
                </p>
              ) : (
                <p className="text-[10px] font-mono mt-1.5 text-slate-500">
                  Cooldown : {action.cooldownDays} j
                </p>
              )}
            </button>
          );
        })}
      </div>
      {feedback && (
        <div
          className={`mt-2 px-3 py-2 rounded-md border text-[11px] flex items-start gap-2 ${
            feedback.ok
              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200'
              : 'bg-amber-500/10 border-amber-500/40 text-amber-200'
          }`}
          data-testid="client-action-feedback"
        >
          {feedback.ok
            ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
          <span>{feedback.message}</span>
        </div>
      )}
    </section>
  );
}
